import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { DashboardHeader } from '../components/dna/DashboardHeader';
import { authFetch, getPaymentInfo } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/components/PremiumPaymentModal.css';

/* ── Constantes ── */
const SESSION_KEY = 'passio_premium_payment';
const SESSION_DURATION = 15 * 60 * 1000;
const MVOLA_LOGO = 'https://i.ibb.co/HfQZPkvw/logo-mvola.png';
const ORANGE_LOGO = 'https://i.ibb.co/VYQZQDrS/logo-orange-money.png';

/* ── Helpers ── */
function formatPhone(value) {
  const cleaned = value.replace(/\D/g, '').slice(0, 10);
  if (cleaned.length >= 3) {
    return cleaned.slice(0, 3) + ' ' + cleaned.slice(3, 5) + ' ' + cleaned.slice(5, 8) + ' ' + cleaned.slice(8);
  }
  return cleaned;
}

function saveSession(data) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...data, savedAt: Date.now(), expiresAt: Date.now() + SESSION_DURATION }));
  } catch { /* silence */ }
}

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (Date.now() > session.expiresAt) { localStorage.removeItem(SESSION_KEY); return null; }
    return session;
  } catch { return null; }
}

function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* silence */ }
}

function generateUSSD(operator, platformNumber, amount, ref) {
  if (operator === 'mvola') return `*111*1*2*${platformNumber}*${amount}*1*${ref}#`;
  return `#144#1*1*${platformNumber}*${platformNumber}*${amount}*1#`;
}

/* ─────────────────────────────────────────────
   PremiumCheckout — Page complète d'activation Premium
   Design : pricing → paiement inline multi-étapes
   ───────────────────────────────────────────── */
export default function PremiumCheckout() {
  const { toggleMobileMenu } = useOutletContext();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [paymentStep, setPaymentStep] = useState(null); // null | 'operator' | 'phone' | 'instructions' | 'submitted'
  const [operator, setOperator] = useState(null);
  const [phone, setPhone] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [timeLeft, setTimeLeft] = useState(SESSION_DURATION / 1000);
  const isRestoring = useRef(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch { /* silence */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Charger les infos de paiement (numéros MVola/Orange)
  useEffect(() => {
    const load = async () => {
      try {
        const info = await getPaymentInfo();
        setPaymentInfo(info);
      } catch (err) {
        console.error('Erreur chargement infos paiement:', err);
      } finally {
        setLoadingInfo(false);
      }
    };
    load();
  }, []);

  // Restaurer session sauvegardée
  useEffect(() => {
    const saved = loadSession();
    if (saved && saved.step !== 'submitted' && saved.unique_code === paymentRequest?.unique_code) {
      isRestoring.current = true;
      if (saved.operator) setOperator(saved.operator);
      if (saved.phone) setPhone(saved.phone);
      if (saved.step === 'phone' || saved.step === 'instructions') setPaymentStep(saved.step);
    }
  }, [paymentRequest]);

  // Sauvegarder la session
  useEffect(() => {
    if (isRestoring.current) { isRestoring.current = false; return; }
    if (paymentStep === 'phone' || paymentStep === 'instructions') {
      saveSession({ unique_code: paymentRequest?.unique_code, step: paymentStep, operator, phone });
    }
    if (paymentStep === 'submitted') clearSession();
  }, [paymentStep, operator, phone, paymentRequest]);

  // Timer instructions
  useEffect(() => {
    if (paymentStep !== 'instructions') return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [paymentStep]);

  const handleUpgrade = async () => {
    // 🔒 Étape 1 : Génération d'un objet local UNIQUEMENT
    // Aucun appel API — la demande ne sera créée en base de données
    // que lorsque l'artiste cliquera sur "J'ai effectué le paiement".
    // Cela évite de saturer l'admin avec des "curieux" qui n'ont pas payé.
    const localCode = 'PREMIUM-' + Math.random().toString(36).substring(2, 8).toUpperCase();
    setPaymentRequest({
      unique_code: localCode,
      amount: status?.premium_price_ariary || 0
    });
    setPaymentStep('operator');
  };

  const handleSelectOperator = (op) => {
    setOperator(op);
    setPaymentStep('phone');
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
    setPhoneError('');
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\s/g, '');
    if (!cleaned || cleaned.length !== 10) { setPhoneError('Veuillez entrer un numéro valide (10 chiffres)'); return; }
    if (!/^03[24-9]/.test(cleaned)) { setPhoneError('Numéro invalide. Doit commencer par 032, 033, 034, 038 ou 039'); return; }
    setPaymentStep('instructions');
    setTimeLeft(SESSION_DURATION / 1000);
  };

  const handleSubmitted = async () => {
    setRequesting(true);
    try {
      const res = await authFetch('/api/subscriptions/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unique_code: paymentRequest?.unique_code || undefined,
          payer_phone: phone.replace(/\s/g, '') || undefined
        })
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentRequest(prev => ({ ...prev, ...data }));
      } else {
        const err = await res.json();
        console.error('Erreur demande Premium:', err);
      }
    } catch (err) {
      console.error('Erreur réseau:', err);
    } finally {
      setRequesting(false);
      setPaymentStep('submitted');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const resetFlow = () => {
    clearSession();
    setPaymentStep(null);
    setPaymentRequest(null);
    setOperator(null);
    setPhone('');
    setPhoneError('');
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const premiumPrice = status?.premium_price_ariary || 0;
  const price = premiumPrice.toLocaleString();
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isSubmitting = requesting && paymentStep === 'instructions';

  const premTracks = status?.premium_track_limit || 30;
  const premCommission = Math.round((status?.premium_commission_rate || 0.15) * 100);
  const freeCommission = Math.round((status?.free_commission_rate || 0.20) * 100);

  if (loading) {
    return (
      <>
        <DesktopHeader onMenu={toggleMobileMenu} title="Abonnement Premium" subtitle="Préparation du checkout…" />
        <div className="dashboard-content">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
          <p className="text-muted mt-3 small">Préparation du checkout...</p>
        </div>
        </div>
      </>
    );
  }

  if (status?.tier === 'premium') {
    return (
      <>
        <DesktopHeader onMenu={toggleMobileMenu} title="Abonnement Premium" subtitle="Votre abonnement est actif" />
        <div className="dashboard-content">
        <div className="text-center py-5">
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>💎</div>
          <h4 className="fw-bold text-navy">Vous êtes déjà Premium</h4>
          <p className="text-muted small mb-3">Votre abonnement est actif. Merci de votre confiance !</p>
          <Link to="/dashboard" className="btn btn-outline-primary fw-semibold rounded-pill px-4">
            Retour au Dashboard
          </Link>
        </div>
        </div>
      </>
    );
  }

  const mvolaConfigured = paymentInfo?.mvola && !paymentInfo?.mvolaUnavailable;
  const orangeConfigured = paymentInfo?.orange && !paymentInfo?.orangeUnavailable;
  const platformNumber = operator === 'mvola' ? paymentInfo?.mvola : paymentInfo?.orange;
  const ussdRef = paymentRequest?.unique_code?.replace(/\D/g, '').slice(-6) || paymentRequest?.unique_code?.slice(0, 6) || '000001';
  const steps = ['operator', 'phone', 'instructions', 'submitted'];
  const currentStepIndex = steps.indexOf(paymentStep);

  return (
    <>
      <DashboardHeader
        title={paymentStep ? 'Finaliser le paiement' : 'Abonnement Premium'}
        subtitle={paymentStep ? `Étape ${currentStepIndex + 1} sur 3` : `Passez au niveau supérieur · ${new Date().getFullYear()}`}
        onMenu={toggleMobileMenu}
      />
      <div className="dashboard-content">

      {/* ══════ SANS PAIEMENT EN COURS — AFFICHAGE MARKETING ══════ */}
      {!paymentStep && (
        <>
          {/* HERO PRICING */}
          <section className="dashboard-section" data-aos="fade-up">
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
              borderRadius: '20px', padding: '3rem 2.5rem', color: '#fff',
              position: 'relative', overflow: 'hidden', textAlign: 'center',
            }}>
              <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(255,255,255,0.15)', padding: '6px 16px',
                  borderRadius: '100px', fontSize: '0.7rem', fontWeight: 600,
                  letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '1.25rem',
                }}>
                  <i className="fa-solid fa-crown" /> Abonnement Premium
                </div>
                <h1 style={{ margin: 0, fontWeight: 800, fontSize: '2.2rem' }}>
                  Passez au niveau supérieur
                </h1>
                <p style={{ margin: '0.5rem auto 0', opacity: 0.85, fontSize: '0.95rem', maxWidth: '480px' }}>
                  {premTracks} tracks/mois, albums illimités, commission à {premCommission}%, paiement J+15 et toutes les fonctionnalités avancées.
                </p>
                <div style={{ margin: '1.5rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1 }}>
                    {price}
                    <span style={{ fontSize: '1rem', fontWeight: 500, opacity: 0.7 }}> Ar</span>
                  </span>
                  <span style={{ fontSize: '1rem', opacity: 0.75 }}>/ mois</span>
                </div>
                <button
                  onClick={handleUpgrade}
                  disabled={requesting}
                  style={{
                    padding: '16px 44px', background: '#fff', color: '#92400e',
                    fontWeight: 700, fontSize: '1rem', borderRadius: '100px',
                    border: 'none', cursor: requesting ? 'not-allowed' : 'pointer',
                    opacity: requesting ? 0.7 : 1,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                    display: 'inline-flex', alignItems: 'center', gap: '10px',
                  }}
                  onMouseEnter={(e) => !requesting && (e.currentTarget.style.transform = 'translateY(-2px)')}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                >
                  {requesting
                    ? <><span className="spinner-border spinner-border-sm" /> Veuillez patienter...</>
                    : <><i className="fa-solid fa-crown" /> Activer Premium maintenant</>
                  }
                </button>
              </div>
            </div>
          </section>

          {/* AVANTAGES PREMIUM */}
          <section className="dashboard-section" data-aos="fade-up">
            <div className="row g-3">
              {[
                { icon: 'fa-music', title: `${premTracks} tracks / mois`, desc: 'Publiez sans vous soucier des limites. Idéal pour les artistes productifs.' },
                { icon: 'fa-compact-disc', title: 'Albums illimités', desc: 'Aucune restriction sur le nombre de projets actifs simultanément.' },
                { icon: 'fa-headphones', title: 'Streaming 96k / 128k', desc: 'Offrez une qualité audio haute définition à vos fans.' },
                { icon: 'fa-chart-line', title: 'Analytics avancés', desc: 'Analysez vos tendances en détail et exportez vos performances en CSV.' },
                { icon: 'fa-percent', title: `Commission ${premCommission}%`, desc: `Gardez ${100 - premCommission}% de vos revenus — boostez vos marges.` },
                { icon: 'fa-wallet', title: 'Paiement J+15', desc: "Touchez l'argent de vos ventes deux fois plus vite." },
                { icon: 'fa-message', title: 'Support prioritaire', desc: 'Réponse garantie sous 12h au lieu de 48h.' },
                { icon: 'fa-circle-check', title: 'Singles gratuits', desc: 'Créez des morceaux gratuits pour fidéliser votre communauté.' },
              ].map((feat, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div style={{
                    display: 'flex', gap: '14px', padding: '1rem 1.25rem',
                    background: '#fff', borderRadius: '14px',
                    border: '1px solid #e9ecef', height: '100%', transition: 'all 0.2s',
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  >
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '12px',
                      background: 'rgba(245,158,11,0.1)', color: '#d97706',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0,
                    }}>
                      <i className={`fa-solid ${feat.icon}`} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#212529' }}>{feat.title}</div>
                      <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '2px' }}>{feat.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* TABLEAU COMPARATIF Free vs Premium */}
          <section className="dashboard-section" data-aos="fade-up">
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '2rem',
              border: '1px solid #e9ecef',
            }}>
              <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#212529', marginBottom: '0.25rem', textAlign: 'center' }}>
                Fonctionnalités comparatives
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6c757d', textAlign: 'center', marginBottom: '1.5rem' }}>
                Ce que vous avez avec le plan Free vs ce que Premium vous apporte
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, color: '#495057' }}>Fonctionnalité</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, color: '#6c757d' }}>Free</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, color: '#d97706' }}>Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Tracks / mois', free: String(status?.free_track_limit || 3), premium: String(premTracks) },
                      { label: 'Albums actifs', free: 'Illimité', premium: 'Illimité' },
                      { label: 'Singles gratuits', free: <i className="fa-solid fa-check" style={{ color: '#16a34a' }}></i>, premium: <i className="fa-solid fa-check" style={{ color: '#16a34a' }}></i> },
                      { label: 'Streaming HLS', free: '64k', premium: '96k / 128k' },
                      { label: 'Analytics', free: 'De base', premium: 'Avancé' },
                      { label: 'Commission', free: `${freeCommission}%`, premium: `${premCommission}%` },
                      { label: 'Paiement', free: 'J+30', premium: 'J+15' },
                      { label: 'Support', free: 'Standard (48h)', premium: 'Prioritaire (12h)' },
                    ].map((row, i) => (
                      <tr key={i} style={{
                        borderBottom: '1px solid #f1f3f5',
                        transition: 'background 0.15s',
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 12px', color: '#495057', fontWeight: 500 }}>{row.label}</td>
                        <td style={{ textAlign: 'center', padding: '10px 12px', color: '#868e96' }}>{row.free}</td>
                        <td style={{ textAlign: 'center', padding: '10px 12px', color: '#d97706', fontWeight: 600 }}>{row.premium}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* COMMENT ÇA MARCHE */}
          <section className="dashboard-section" data-aos="fade-up">
            <div style={{ background: '#f8f9fa', borderRadius: '16px', padding: '2rem' }}>
              <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#212529', marginBottom: '1.5rem', textAlign: 'center' }}>
                Comment ça marche ?
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                {[
                  { step: '1', title: 'Cliquez sur le bouton', desc: 'Activez votre abonnement en un clic. Un code unique vous sera fourni.' },
                  { step: '2', title: 'Effectuez le paiement', desc: 'Transférez le montant via MVola, Orange Money ou Airtel Money avec le code en référence.' },
                  { step: '3', title: 'Activation sous 24h', desc: 'Notre équipe vérifie et active votre compte Premium. Vous recevez une confirmation.' },
                ].map((item, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff',
                      fontSize: '1.2rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 0.75rem', boxShadow: '0 4px 12px rgba(245,158,11,0.25)',
                    }}>
                      {item.step}
                    </div>
                    <h5 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#212529', marginBottom: '0.25rem' }}>{item.title}</h5>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', margin: 0 }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ══════ FLUX PAIEMENT INLINE (pleine page) ══════ */}
      {paymentStep && (
        <section className="dashboard-section" data-aos="fade-up">
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            {/* Step Indicator */}
            {paymentStep !== 'submitted' && (
              <div className="ppm-steps">
                {steps.filter(s => s !== 'submitted').map((s, i) => (
                  <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    <span className={`ppm-step-dot ${i < currentStepIndex ? 'done' : i === currentStepIndex ? 'active' : ''}`} />
                    {i < steps.filter(x => x !== 'submitted').length - 1 && (
                      <span className={`ppm-step-line ${i < currentStepIndex ? 'done' : ''}`} />
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* ÉTAPE 1 : CHOIX OPÉRATEUR */}
            {paymentStep === 'operator' && (
              <>
                <div className="ppm-header">
                  <h2>Activer Premium</h2>
                  <p className="ppm-subtitle">Choisissez votre opérateur Mobile Money</p>
                </div>
                <div className="ppm-price-card">
                  <span className="ppm-price-label">Abonnement Premium</span>
                  <span className="ppm-price-value">{price} Ar/mois</span>
                </div>
                <h3 className="ppm-operator-heading">Choisissez votre opérateur</h3>
                {loadingInfo ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Chargement...</div>
                ) : (
                  <div className="ppm-operators">
                    <button className="ppm-operator-btn" onClick={() => handleSelectOperator('mvola')} disabled={!mvolaConfigured}>
                      <img src={MVOLA_LOGO} alt="MVola" className="ppm-operator-logo" />
                      <span className="ppm-operator-name">MVola</span>
                      {!mvolaConfigured && <span className="ppm-operator-disabled">Temporairement indisponible</span>}
                    </button>
                    <button className="ppm-operator-btn" onClick={() => handleSelectOperator('orange')} disabled={!orangeConfigured}>
                      <img src={ORANGE_LOGO} alt="Orange Money" className="ppm-operator-logo" />
                      <span className="ppm-operator-name">Orange Money</span>
                      {!orangeConfigured && <span className="ppm-operator-disabled">Temporairement indisponible</span>}
                    </button>
                  </div>
                )}
                <div className="ppm-btn-group" style={{ marginTop: '1rem' }}>
                  <button className="ppm-btn ppm-btn-secondary" onClick={resetFlow}>Annuler</button>
                </div>
              </>
            )}

            {/* ÉTAPE 2 : NUMÉRO DE TÉLÉPHONE */}
            {paymentStep === 'phone' && (
              <>
                <div className="ppm-header">
                  <h2>Activer Premium</h2>
                  <p className="ppm-subtitle">Avec quel numéro allez-vous payer ?</p>
                </div>
                <div className="ppm-price-card">
                  <span className="ppm-price-label">Abonnement Premium</span>
                  <span className="ppm-price-value">{price} Ar/mois</span>
                </div>
                <div className="ppm-selected-op">
                  <img src={operator === 'mvola' ? MVOLA_LOGO : ORANGE_LOGO} alt={operator === 'mvola' ? 'MVola' : 'Orange Money'} />
                  <span>{operator === 'mvola' ? 'MVola' : 'Orange Money'}</span>
                  <button className="ppm-change-op" onClick={() => setPaymentStep('operator')} type="button">Changer</button>
                </div>
                <form onSubmit={handlePhoneSubmit}>
                  <div className="ppm-form-group">
                    <label htmlFor="ppm-phone">Votre numéro Mobile Money</label>
                    <input type="tel" id="ppm-phone" value={phone} onChange={handlePhoneChange} placeholder="034 XX XXX XX" autoFocus />
                    <span className="ppm-form-hint">Format: 034 XX XXX XX ou 032 XX XXX XX</span>
                  </div>
                  {phoneError && (
                    <div style={{ background: '#fef2f2', color: '#dc2626', padding: '0.7rem 1rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '1rem', border: '1px solid #fca5a5' }}>
                      {phoneError}
                    </div>
                  )}
                  <button type="submit" className="ppm-btn ppm-btn-primary" disabled={phone.replace(/\s/g, '').length !== 10}>
                    <i className="fa-solid fa-arrow-right" /> Continuer
                  </button>
                </form>
                <div className="ppm-btn-group" style={{ marginTop: '0.75rem' }}>
                  <button className="ppm-btn ppm-btn-secondary" onClick={resetFlow}>Annuler</button>
                </div>
              </>
            )}

            {/* ÉTAPE 3 : INSTRUCTIONS PAIEMENT */}
            {paymentStep === 'instructions' && paymentRequest && (
              <>
                <div className="ppm-header">
                  <h2>Effectuer le paiement</h2>
                  <p className="ppm-subtitle">via {operator === 'mvola' ? 'MVola' : 'Orange Money'}</p>
                </div>

                <div className={`ppm-countdown ${timeLeft < 300 ? 'urgent' : ''}`}>
                  <div className="ppm-countdown-label">Temps restant</div>
                  <div className="ppm-countdown-value">{formatTime(timeLeft)}</div>
                  {timeLeft < 300 && <div className="ppm-countdown-warn">Dépêchez-vous !</div>}
                </div>

                <div className="ppm-details">
                  <div className="ppm-detail-row">
                    <span className="ppm-detail-label">Montant</span>
                    <strong className="ppm-detail-value">{Number(paymentRequest.amount || premiumPrice).toLocaleString()} Ar</strong>
                  </div>
                  <div className="ppm-detail-row">
                    <span className="ppm-detail-label">Numéro à créditer</span>
                    <strong className="ppm-detail-value">
                      <a href={`tel:${platformNumber}`} style={{ color: '#d97706', textDecoration: 'none' }}>{platformNumber}</a>
                    </strong>
                  </div>
                  <div className="ppm-detail-row">
                    <span className="ppm-detail-label">Code de référence</span>
                    <strong className="ppm-ref-value">{paymentRequest.unique_code}</strong>
                  </div>
                  <div className="ppm-detail-row ppm-detail-highlight" style={{ marginTop: '0.5rem' }}>
                    <span className="ppm-detail-label" style={{ color: '#92400e', fontWeight: 600 }}>Référence USSD</span>
                    <strong style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: '#92400e', letterSpacing: '1px' }}>{ussdRef}</strong>
                  </div>
                </div>

                <div className="ppm-qr-section">
                  <h4>Scannez pour payer automatiquement</h4>
                  <div className="ppm-qr-wrap">
                    <QRCodeSVG value={generateUSSD(operator, platformNumber, paymentRequest.amount || premiumPrice, ussdRef)} size={160} bgColor="#ffffff" fgColor="#000000" level="M" />
                  </div>
                  <p className="ppm-qr-hint">Scannez avec votre appareil photo ou Google Lens</p>
                  {!isAndroid && (
                    <div className="ppm-ussd-text">
                      <p className="ppm-ussd-text-label">Ou saisissez manuellement :</p>
                      <code className="ppm-ussd-code">{generateUSSD(operator, platformNumber, paymentRequest.amount || premiumPrice, ussdRef)}</code>
                      <p className="ppm-ussd-text-hint">
                        {operator === 'mvola'
                          ? <>Le code <strong>{ussdRef}</strong> est déjà inclus. Validez avec votre code secret.</>
                          : <>Validez avec votre code secret Orange Money.</>
                        }
                      </p>
                    </div>
                  )}
                </div>

                <div className="ppm-copy-grid">
                  <button className={`ppm-copy-btn ${copiedField === 'number' ? 'copied' : ''}`} onClick={() => copyToClipboard(platformNumber, 'number')}>
                    <i className="fa-regular fa-copy" /><span>{copiedField === 'number' ? 'Copié !' : 'Numéro'}</span>
                  </button>
                  <button className={`ppm-copy-btn ${copiedField === 'amount' ? 'copied' : ''}`} onClick={() => copyToClipboard(String(paymentRequest.amount || premiumPrice), 'amount')}>
                    <i className="fa-regular fa-copy" /><span>{copiedField === 'amount' ? 'Copié !' : 'Montant'}</span>
                  </button>
                  <button className={`ppm-copy-btn ${copiedField === 'code' ? 'copied' : ''}`} onClick={() => copyToClipboard(paymentRequest.unique_code, 'code')}>
                    <i className="fa-regular fa-copy" /><span>{copiedField === 'code' ? 'Copié !' : 'Code'}</span>
                  </button>
                </div>

                {isAndroid && (
                  <>
                    <div className="ppm-ussd-inline">
                      <span className="ppm-ussd-inline-label">Code USSD :</span>
                      <code className="ppm-ussd-inline-code">{generateUSSD(operator, platformNumber, paymentRequest.amount || premiumPrice, ussdRef)}</code>
                    </div>
                    <a href={`tel:${encodeURIComponent(generateUSSD(operator, platformNumber, paymentRequest.amount || premiumPrice, ussdRef))}`} className="ppm-tap-btn">
                      <img src={operator === 'mvola' ? MVOLA_LOGO : ORANGE_LOGO} alt={operator === 'mvola' ? 'MVola' : 'Orange Money'} className="ppm-tap-btn-logo" />
                      <span className="ppm-tap-btn-text">Appeler pour payer</span>
                      <i className="fa-solid fa-phone" />
                    </a>
                  </>
                )}

                <div className="ppm-instructions">
                  <h4>Comment payer manuellement :</h4>
                  <ol>
                    {operator === 'mvola' ? (
                      <>
                        <li>Composez <strong>*111#</strong></li>
                        <li>Choisissez <strong>Transfert d'argent</strong></li>
                        <li>Entrez le numéro : <strong>{platformNumber}</strong></li>
                        <li>Montant : <strong>{Number(paymentRequest.amount || premiumPrice).toLocaleString()} Ar</strong></li>
                        <li>Référence : <strong>{paymentRequest.unique_code}</strong></li>
                        <li>Validez avec votre code secret MVola</li>
                      </>
                    ) : (
                      <>
                        <li>Composez <strong>#144#</strong></li>
                        <li>Choisissez <strong>Transfert d'argent</strong></li>
                        <li>Entrez le numéro : <strong>{platformNumber}</strong></li>
                        <li>Montant : <strong>{Number(paymentRequest.amount || premiumPrice).toLocaleString()} Ar</strong></li>
                        <li>Validez avec votre code secret Orange Money</li>
                      </>
                    )}
                  </ol>
                </div>

                <div style={{ padding: '0.85rem 1rem', background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.04))', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '10px', marginBottom: '1rem', textAlign: 'center', fontSize: '0.82rem', color: '#92400e' }}>
                  <i className="fa-solid fa-clock me-1" />
                  <strong>Activation manuelle :</strong> Notre équipe vérifie votre paiement et active votre compte Premium sous <strong>24h</strong>.
                </div>

                <div className="ppm-btn-group">
                  <button className="ppm-btn ppm-btn-primary" onClick={handleSubmitted} disabled={isSubmitting}>
                    {isSubmitting
                      ? <><span className="spinner-border spinner-border-sm" /> Envoi en cours...</>
                      : <><i className="fa-solid fa-check" /> J'ai effectué le paiement</>
                    }
                  </button>
                  <button className="ppm-btn ppm-btn-secondary" onClick={resetFlow} disabled={isSubmitting}>Annuler</button>
                </div>
              </>
            )}

            {/* ÉTAPE 4 : CONFIRMÉ */}
            {paymentStep === 'submitted' && (
              <>
                <div className="ppm-header">
                  <div className="ppm-success-icon">✓</div>
                  <h2>Demande envoyée !</h2>
                  <p className="ppm-subtitle" style={{ maxWidth: '320px', margin: '0.35rem auto 0' }}>
                    Votre demande d'activation Premium a bien été enregistrée.
                  </p>
                </div>
                <div className="ppm-submitted-info">
                  <p style={{ marginBottom: '0.25rem' }}><i className="fa-solid fa-circle-check" style={{ color: '#16a34a' }} /> Paiement de <strong>{price} Ar</strong></p>
                  <p>via <strong>{operator === 'mvola' ? 'MVola' : 'Orange Money'}</strong>{phone && ` • ${phone}`}</p>
                  <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fff', borderRadius: '8px', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem', color: '#92400e', letterSpacing: '2px' }}>
                    Code : {paymentRequest?.unique_code}
                  </div>
                </div>
                <div style={{ padding: '0.85rem 1rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '10px', marginBottom: '1rem', textAlign: 'center', fontSize: '0.82rem', color: '#92400e' }}>
                  <i className="fa-solid fa-clock me-1" />
                  Notre équipe vérifie votre paiement et active votre compte sous <strong>24h</strong>. Vous recevrez une confirmation par email.
                </div>
                <Link to="/dashboard" className="ppm-btn ppm-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
                  <i className="fa-solid fa-check" /> Retour au Dashboard
                </Link>
              </>
            )}
          </div>
        </section>
      )}
      </div>
    </>
  );
}

/* ── Sous-composants headers ── */
function MobileHeader() {
  return null;
}

function DesktopHeader({ onMenu, title = 'Abonnement Premium', subtitle = 'Passez au niveau supérieur' }) {
  return <DashboardHeader title={title} subtitle={subtitle} onMenu={onMenu} />;
}
